/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { MouseEventHandler } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
    Button,
    FauxImage,
    FauxUnavailableImage,
    Placeholder,
    STATUS,
    useAssetImages,
} from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import { DATE_FORMAT } from '../../constants';

import { formatPrice } from '../../utils';
import type {
    TreePermitData,
    SeasonData,
    Reservation,
    PermitType,
} from '../../../types/permit';

interface SummaryTypes {
    onSubmitHandler: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    reservations: Array<Reservation>;
    total?: number;
    treePermit: Partial<TreePermitData>;
    isSubmitting?: boolean;
    isMobile?: boolean;
    permitTypes: Array<PermitType>;
    seasons: Record<string, Partial<SeasonData>>;
}

export default function SummaryTypes({
    onSubmitHandler,
    reservations,
    isSubmitting,
    total,
    isMobile,
    permitTypes,
    seasons,
    treePermit,
}: SummaryTypes) {
    const { previewImage, status } = useAssetImages({ assetId: treePermit?.asset_id });
    const hasReservations = !!reservations.length;
    const reservationFee = reservations
        .map((item) => item?.permit?.fees ?? 0)
        .reduce((a, b) => a + b, 0);
    const totalValue = (total ?? 0) + reservationFee;
    const renderHeader = () => {
        let headerImage;

        if (previewImage?.url) {
            headerImage = (
                <FauxImage
                    src={previewImage?.url}
                    altText={previewImage?.altText}
                    className="tp-summary-image"
                />
            );
        } else if (status === STATUS.IDLE || status === STATUS.PENDING) {
            headerImage = <Placeholder className="tp-summary-image" height="500px" />;
        } else {
            headerImage = <FauxUnavailableImage className="tp-summary-image" />;
        }

        return headerImage;
    };
    const renderReservationCards = () => {
        return reservations.map((item, key) => {
            const currentSeason = seasons[item?.permit?.id];
            const currentPermitType = permitTypes.find(
                (val: any) => val?.id === item?.permit?.id
            );
            const originalPrice = currentPermitType?.price ?? 0;
            const hasEkopass = !isEmpty(item?.holder?.voucher);
            const reservationNumber = key + 1;
            const price = item?.permit?.price;
            const subtotal = price;
            const startDate = gnoment(currentSeason?.start_date).format(DATE_FORMAT);
            const endDate = gnoment(currentSeason?.end_date).format(DATE_FORMAT);
            const quantity = item?.quantity;
            const priceWithCondition = hasEkopass ? originalPrice * quantity : price;
            return (
                <div className="tp-reservation-card" key={key}>
                    <div className="tp-reservation-card-header">
                        <div className="tp-reservation-card-title">
                            {reservationNumber}. {item?.permit?.name}
                        </div>
                        <div className="tp-reservation-card-description">
                            {treePermit?.name}
                        </div>
                    </div>
                    <div className="tp-reservation-card-body">
                        <div className="tp-summary-metadata">
                            <div className="tp-summary-metadata-item">
                                <div className="tp-summary-metadata-title">
                                    Start Date
                                </div>
                                <div className="tp-summary-metadata-description">
                                    {startDate}
                                </div>
                            </div>
                            <div className="tp-summary-metadata-item">
                                <div className="tp-summary-metadata-title">
                                    Valid Through
                                </div>
                                <div className="tp-summary-metadata-description">
                                    {endDate}
                                </div>
                            </div>
                        </div>
                        <div className="tp-summary-metadata tp-summary-metadata-horizontal">
                            <div className="tp-summary-metadata-item">
                                <div className="tp-summary-metadata-title">
                                    Tree(s) x{quantity}
                                </div>
                                <div className="tp-summary-metadata-description tp-summary-metadata-description-small">
                                    {formatPrice(priceWithCondition)}
                                </div>
                            </div>
                            {hasEkopass && (
                                <div className="tp-summary-metadata-item">
                                    <div className="tp-summary-metadata-title">
                                        EKO Pass
                                    </div>
                                    <div className="tp-summary-metadata-description tp-summary-metadata-description-small">
                                        - {formatPrice(originalPrice)}
                                    </div>
                                </div>
                            )}
                            <div className="tp-summary-metadata-item">
                                <div className="tp-summary-metadata-title">Subtotal</div>
                                <div className="tp-summary-metadata-description">
                                    {formatPrice(subtotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };
    const renderFee = () => {
        return (
            <div className="tp-summary-fee">
                <div className="tp-summary-fee-container">
                    <div className="tp-summary-fee-title">Service Fee</div>
                    <div className="tp-summary-fee-description">
                        {formatPrice(reservationFee)}
                    </div>
                </div>
            </div>
        );
    };
    const renderTotal = () => {
        return (
            <div className="tp-summary-total">
                <div className="tp-summary-total-title">Total Cost</div>
                <div className="tp-summary-total-description">
                    {formatPrice(totalValue)}
                </div>
            </div>
        );
    };
    const renderBody = () => {
        if (!hasReservations) {
            return (
                <div className="tp-summary-message">
                    No Tree Permits have been selected.
                </div>
            );
        }

        return (
            <>
                {renderReservationCards()}
                {renderFee()}
                {renderTotal()}
            </>
        );
    };

    return (
        <div className="tp-summary">
            <div className="tp-summary-header">{renderHeader()}</div>
            <div className="tp-summary-body">{renderBody()}</div>
            <div className="tp-summary-footer">
                {!isMobile && (
                    <div className="tp-summary-controls">
                        <Button
                            className="tp-summary-cta"
                            isDisabled={!hasReservations || isSubmitting}
                            onClick={onSubmitHandler}
                        >
                            Proceed to Cart
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
